import React from 'react'

// Dependency-free responsive line chart. Clean by default; hovering a point
// shows a guide line + that point's exact value (so the trend stays readable
// without a wall of overlapping labels). Preserves given row order.

const VB_W = 1000
const VB_H = 300
const PAD = { top: 26, right: 24, bottom: 78, left: 48 }

const niceMax = (value) => {
  if (!value || value <= 0) return 10
  const pow = Math.pow(10, Math.floor(Math.log10(value)))
  const n = value / pow
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * pow
}

const LineChart = ({ rows, xKey, yKey, color = '#5b9bd5' }) => {
  const [hover, setHover] = React.useState(null)

  const data = (rows || []).map((r) => ({
    x: r[xKey] == null ? '' : String(r[xKey]).trim(),
    y: Number(r[yKey]) || 0
  }))
  if (!data.length) return null

  const innerW = VB_W - PAD.left - PAD.right
  const innerH = VB_H - PAD.top - PAD.bottom
  const maxY = niceMax(Math.max(...data.map((d) => d.y)))
  const ticks = 4
  const labelEvery = Math.ceil(data.length / 24)
  const gap = data.length > 1 ? innerW / (data.length - 1) : innerW

  const xFor = (i) =>
    data.length > 1 ? PAD.left + gap * i : PAD.left + innerW / 2
  const yFor = (v) => PAD.top + innerH - (v / maxY) * innerH

  const points = data.map((d, i) => ({ px: xFor(i), py: yFor(d.y), ...d }))
  const path = points.map((p) => `${p.px},${p.py}`).join(' ')
  const hp = hover != null ? points[hover] : null

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {/* y gridlines + labels */}
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const val = (maxY / ticks) * i
        const y = yFor(val)
        return (
          <g key={`g${i}`}>
            <line x1={PAD.left} x2={VB_W - PAD.right} y1={y} y2={y} stroke="#eceff1" />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#90a4ae">
              {Math.round(val).toLocaleString()}
            </text>
          </g>
        )
      })}

      {/* hovered guide line */}
      {hp ? (
        <line
          x1={hp.px}
          x2={hp.px}
          y1={PAD.top}
          y2={PAD.top + innerH}
          stroke="#cfd8dc"
          strokeDasharray="3 3"
        />
      ) : null}

      {/* the line */}
      <polyline
        points={path}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* markers + rotated x labels */}
      {points.map((p, i) => {
        const showLabel = i % labelEvery === 0
        return (
          <g key={`p${i}`}>
            <circle cx={p.px} cy={p.py} r="2.5" fill={color} />
            {showLabel ? (
              <text
                x={p.px}
                y={PAD.top + innerH + 14}
                textAnchor="end"
                fontSize="10"
                fill="#607d8b"
                transform={`rotate(-45 ${p.px} ${PAD.top + innerH + 14})`}
              >
                {p.x}
              </text>
            ) : null}
          </g>
        )
      })}

      {/* hovered point: emphasised dot + value */}
      {hp ? (
        <g>
          <circle cx={hp.px} cy={hp.py} r="4.5" fill={color} stroke="#fff" strokeWidth="1.5" />
          <text
            x={Math.min(Math.max(hp.px, PAD.left + 16), VB_W - PAD.right - 16)}
            y={Math.max(hp.py - 12, PAD.top + 4)}
            textAnchor="middle"
            fontSize="13"
            fontWeight="600"
            fill="#37474f"
          >
            {hp.y.toLocaleString()}
          </text>
        </g>
      ) : null}

      {/* baseline */}
      <line
        x1={PAD.left}
        x2={VB_W - PAD.right}
        y1={PAD.top + innerH}
        y2={PAD.top + innerH}
        stroke="#b0bec5"
      />

      {/* transparent hit areas for hover */}
      {points.map((p, i) => (
        <rect
          key={`h${i}`}
          x={p.px - gap / 2}
          y={PAD.top}
          width={gap}
          height={innerH}
          fill="transparent"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
    </svg>
  )
}

export default LineChart
